import type { CampaignStat } from "@/types/clicks";
import {
  attributeOf,
  getDimensionLabel,
  getMetricDisplay,
  getMetricNumericValue,
  metricLabel,
  type GridColumn,
  type MetricFieldId,
} from "./metricDisplay";

type Cell = string | number;

/**
 * The Clicks CSV as a header row plus value rows, for Papa.unparse.
 *
 * - The grouping column(s) come first: under multi-dim grouping each part is
 *   its own column (Country, Brand, ...), then the visible columns in their
 *   on-screen order.
 * - Raw numbers, no currency symbol or rounding, so Excel reads them as numbers.
 * - An empty cell exactly where the table shows "—" (no GSC data, no keyword
 *   volume, a formula with nothing to compute). It used to be 0, which reads
 *   as a real zero.
 * - Arrays, not objects keyed by header, so two columns with the same header
 *   (a custom metric named like a built-in one) both survive.
 */
export function clicksCsv(
  rows: CampaignStat[],
  opts: { groupDims?: string[]; dimensionLabel: string; columns: GridColumn[] },
): { fields: string[]; data: Cell[][] } {
  type Col = { header: string; get: (r: CampaignStat) => Cell };
  const dims = opts.groupDims ?? [];
  const dimCols: Col[] =
    dims.length >= 2
      ? dims.map((d, i) => ({ header: getDimensionLabel(d), get: (r) => r.dimension_parts?.[i] ?? "" }))
      : [{ header: opts.dimensionLabel, get: (r) => r.dimension_value }];
  const middleCols: Col[] = opts.columns.map((col) => {
    if (col.kind === "brand") return { header: "Brand", get: (r) => r.dimension_brand ?? "" };
    if (col.kind === "campaign_group")
      return { header: "Campaign Group", get: (r) => r.dimension_campaign_group ?? "" };
    if (col.kind === "attr") {
      // Raw, like the metrics: a date as 2027-07-31, the USD amount as a number.
      const attr = col.id;
      return {
        header: metricLabel(attr),
        get: (r) => {
          const v = attributeOf(r, attr);
          return typeof v === "number" || typeof v === "string" ? v : "";
        },
      };
    }
    const id = col.metric.id;
    const { nullable } = getMetricDisplay(id);
    return {
      header: metricLabel(id, col.metric.label),
      get: (r) =>
        nullable && r[id as keyof CampaignStat] == null ? "" : getMetricNumericValue(r, id as MetricFieldId),
    };
  });
  const cols = [...dimCols, ...middleCols];
  return { fields: cols.map((c) => c.header), data: rows.map((r) => cols.map((c) => c.get(r))) };
}
